import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent, type ReactNode } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  acceptanceTerms,
  termsOfUse,
  type TermsAcceptanceKind,
  type TermsAudience,
  type TermsSettings,
  type TermsVersion,
  type TermsVersionInput,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { FieldLabel } from '@/shared/ui/FieldLabel';
import { Modal } from '@/shared/ui/Modal';
import { RichTextEditor, RichTextView } from '@/shared/ui/RichTextEditor';
import { Select } from '@/shared/ui/Select';
import { SkeletonText } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';
import { CHAVE_DO_TERMO_VIGENTE } from './TermsOfUseModal';
import {
  PERMISSAO_APROVAR_TERMO,
  PERMISSAO_EDITAR_TERMO,
  ROTULO_DO_PUBLICO,
  ROTULO_DO_TIPO,
  TIPOS_DE_TERMO_DE_ACEITE,
} from './rotulos';

/**
 * Aba "Termos" das configuracoes (TER-012): o aceite do termo de uso, as versoes do
 * termo de uso e as do termo de aceite da proposta, por tipo.
 *
 * O texto e da seguradora: nada nasce preenchido. Cada versao segue o padrao de
 * versao com aprovacao: o rascunho e um so, quem escreve nao ativa, a ativacao pede
 * justificativa e a versao anterior fica guardada como revogada, com a vigencia.
 */
export function TermsSettingsPanel() {
  const { can } = useAuth();
  const podeEditar = can(PERMISSAO_EDITAR_TERMO);
  const podeAprovar = can(PERMISSAO_APROVAR_TERMO);
  const [tipo, setTipo] = useState<TermsAcceptanceKind>('emissao');

  return (
    <div className="flex flex-col gap-5">
      <ConfiguracaoDoAceite podeAprovar={podeAprovar} />

      <VersoesDoTermo
        titulo="Termo de uso"
        descricao="O termo que a pessoa aceita no primeiro acesso e a cada versao nova."
        chave={['terms-of-use', 'versions']}
        serie={{
          listar: (signal) => termsOfUse.list(signal),
          criar: (entrada) => termsOfUse.create(entrada),
          editar: (id, entrada) => termsOfUse.update(id, entrada),
          ativar: (id, justificativa) => termsOfUse.activate(id, justificativa),
          revogar: (id, motivo) => termsOfUse.revoke(id, motivo),
        }}
        podeEditar={podeEditar}
        podeAprovar={podeAprovar}
      />

      <VersoesDoTermo
        key={tipo}
        titulo="Termo de aceite da proposta"
        descricao="O texto do Termo e declaracao, com a caixa Declaro que li e estou ciente das condicoes acima."
        chave={['acceptance-terms', tipo]}
        seletor={
          <Select
            label="Tipo de termo de aceite"
            className="h-9"
            options={TIPOS_DE_TERMO_DE_ACEITE.map((item) => ({
              value: item,
              label: ROTULO_DO_TIPO[item],
            }))}
            value={tipo}
            onChange={(evento) => setTipo(evento.target.value as TermsAcceptanceKind)}
          />
        }
        serie={{
          listar: (signal) => acceptanceTerms.list(tipo, signal),
          criar: (entrada) => acceptanceTerms.create(tipo, entrada),
          editar: (id, entrada) => acceptanceTerms.update(id, entrada),
          ativar: (id, justificativa) => acceptanceTerms.activate(id, justificativa),
          revogar: (id, motivo) => acceptanceTerms.revoke(id, motivo),
        }}
        podeEditar={podeEditar}
        podeAprovar={podeAprovar}
      />
    </div>
  );
}

// --- Configuracao do aceite --------------------------------------------------------

function ConfiguracaoDoAceite({ podeAprovar }: { podeAprovar: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const idDaChave = useId();
  const consulta = useQuery({
    queryKey: ['terms-of-use', 'settings'],
    queryFn: ({ signal }) => termsOfUse.settings(signal),
  });
  const [form, setForm] = useState<Pick<
    TermsSettings,
    'acceptance_required' | 'audience'
  > | null>(null);
  const atual = form ?? consulta.data ?? null;

  const salvar = useMutation({
    mutationFn: (entrada: Pick<TermsSettings, 'acceptance_required' | 'audience'>) =>
      termsOfUse.updateSettings(entrada),
    onSuccess: () => {
      toast.success('Configuracao do aceite gravada.');
      setForm(null);
      void queryClient.invalidateQueries({ queryKey: ['terms-of-use', 'settings'] });
      void queryClient.invalidateQueries({ queryKey: CHAVE_DO_TERMO_VIGENTE });
    },
  });

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (atual) {
      salvar.mutate({
        acceptance_required: atual.acceptance_required,
        audience: atual.audience,
      });
    }
  }

  return (
    <Card>
      <CardHeader
        title="Aceite do termo de uso"
        description="Se o aceite bloqueia o uso da plataforma ate o clique, e para quem o termo vale."
      />
      <CardBody>
        {consulta.isPending ? <SkeletonText lines={3} /> : null}
        {consulta.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar a configuracao">
            {consulta.error.message}
          </Alert>
        ) : null}
        {atual ? (
          <form className="flex flex-col gap-4" onSubmit={enviar}>
            <Alert tone="info" title="Regra pendente">
              O aceite e pedido no primeiro acesso e a cada versao (CPO-021). A regra
              ainda nao foi homologada pela seguradora, e por isso o bloqueio nasce
              desligado. Ligar faz a moldura bloquear o uso de quem ainda nao aceitou a
              versao vigente.
            </Alert>
            <div className="flex items-start gap-2.5">
              <input
                id={idDaChave}
                type="checkbox"
                role="switch"
                aria-checked={atual.acceptance_required}
                className="mt-0.5 size-4 accent-[var(--color-brand-500)]"
                checked={atual.acceptance_required}
                disabled={!podeAprovar}
                onChange={(evento) =>
                  setForm({ ...atual, acceptance_required: evento.target.checked })
                }
              />
              <FieldLabel
                htmlFor={idDaChave}
                label="Bloquear o uso ate o aceite"
                className="text-sm text-ink-800"
              />
            </div>
            <Select
              label="Publico do termo"
              options={(['corretora', 'todos'] as TermsAudience[]).map((item) => ({
                value: item,
                label: ROTULO_DO_PUBLICO[item],
              }))}
              value={atual.audience}
              disabled={!podeAprovar}
              onChange={(evento) =>
                setForm({ ...atual, audience: evento.target.value as TermsAudience })
              }
            />
            {consulta.data?.updated_label ? (
              <p className="text-xs text-ink-500">
                Alterada por {consulta.data.updated_label} em{' '}
                {formatDateTime(consulta.data.updated_at)}.
              </p>
            ) : null}
            {salvar.error ? (
              <Alert tone="danger" title="Nao foi possivel gravar">
                {salvar.error.message}
              </Alert>
            ) : null}
            {podeAprovar ? (
              <div className="flex justify-end">
                <Button
                  type="submit"
                  loading={salvar.isPending}
                  disabled={form === null}
                >
                  Salvar configuracao
                </Button>
              </div>
            ) : (
              <p className="text-xs text-ink-500">
                Mudar a configuracao exige a alcada de aprovacao do termo.
              </p>
            )}
          </form>
        ) : null}
      </CardBody>
    </Card>
  );
}

// --- Versoes ---------------------------------------------------------------------

interface SerieDeTermo {
  listar: (signal?: AbortSignal) => Promise<TermsVersion[]>;
  criar: (entrada: TermsVersionInput) => Promise<TermsVersion>;
  editar: (id: string, entrada: TermsVersionInput) => Promise<TermsVersion>;
  ativar: (id: string, justificativa: string) => Promise<TermsVersion>;
  revogar: (id: string, motivo: string) => Promise<TermsVersion>;
}

type Acao =
  | { tipo: 'ver'; versao: TermsVersion }
  | { tipo: 'editar'; versao: TermsVersion | null }
  | { tipo: 'ativar'; versao: TermsVersion }
  | { tipo: 'revogar'; versao: TermsVersion };

function vigencia(versao: TermsVersion): string {
  if (!versao.effective_from) return '-';
  const inicio = formatDateTime(versao.effective_from);
  return versao.effective_until
    ? `${inicio} ate ${formatDateTime(versao.effective_until)}`
    : `desde ${inicio}`;
}

function VersoesDoTermo({
  titulo,
  descricao,
  chave,
  serie,
  seletor,
  podeEditar,
  podeAprovar,
}: {
  titulo: string;
  descricao: string;
  chave: readonly unknown[];
  serie: SerieDeTermo;
  seletor?: ReactNode;
  podeEditar: boolean;
  podeAprovar: boolean;
}) {
  const queryClient = useQueryClient();
  const [acao, setAcao] = useState<Acao | null>(null);
  const consulta = useQuery({
    queryKey: chave,
    queryFn: ({ signal }) => serie.listar(signal),
  });
  const versoes = consulta.data ?? [];
  const rascunho = versoes.find((versao) => versao.status === 'rascunho');
  const ativa = versoes.find((versao) => versao.status === 'ativa');

  function aposMudar() {
    setAcao(null);
    void queryClient.invalidateQueries({ queryKey: chave });
    void queryClient.invalidateQueries({ queryKey: CHAVE_DO_TERMO_VIGENTE });
  }

  return (
    <Card>
      <CardHeader
        title={titulo}
        description={descricao}
        action={
          podeEditar ? (
            <Button
              size="sm"
              disabled={rascunho !== undefined}
              onClick={() => setAcao({ tipo: 'editar', versao: null })}
            >
              Novo rascunho
            </Button>
          ) : null
        }
      />
      {seletor ? <div className="border-b border-line px-5 py-4">{seletor}</div> : null}
      {consulta.isError ? (
        <div className="px-5 py-4">
          <Alert tone="danger" title="Nao foi possivel carregar as versoes">
            {consulta.error.message}
          </Alert>
        </div>
      ) : null}
      <DataTable
        columns={[
          {
            key: 'versao',
            header: 'Versao',
            render: (v: TermsVersion) => `Versao ${v.version}`,
          },
          {
            key: 'situacao',
            header: 'Situacao',
            render: (v: TermsVersion) => <StatusBadge kind="versao" value={v.status} />,
          },
          {
            key: 'titulo',
            header: 'Titulo',
            render: (v: TermsVersion) => v.title || '-',
          },
          {
            key: 'vigencia',
            header: 'Vigencia',
            hideBelow: 'md',
            render: (v: TermsVersion) => vigencia(v),
          },
          {
            key: 'autoria',
            header: 'Aberta por',
            hideBelow: 'lg',
            render: (v: TermsVersion) => v.created_label || '-',
          },
          {
            key: 'aprovacao',
            header: 'Ativada por',
            hideBelow: 'lg',
            render: (v: TermsVersion) => v.activated_label || '-',
          },
          {
            key: 'acoes',
            header: 'Acoes',
            align: 'right',
            render: (v: TermsVersion) => (
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setAcao({ tipo: 'ver', versao: v })}
                >
                  Ver texto
                </Button>
                {v.status === 'rascunho' && podeEditar ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setAcao({ tipo: 'editar', versao: v })}
                  >
                    Editar
                  </Button>
                ) : null}
                {v.status === 'rascunho' && podeAprovar ? (
                  <Button
                    size="sm"
                    onClick={() => setAcao({ tipo: 'ativar', versao: v })}
                  >
                    Ativar
                  </Button>
                ) : null}
                {v.status === 'ativa' && podeAprovar ? (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => setAcao({ tipo: 'revogar', versao: v })}
                  >
                    Revogar
                  </Button>
                ) : null}
              </div>
            ),
          },
        ]}
        items={versoes}
        keyOf={(v) => v.id}
        loading={consulta.isPending}
        empty={
          <EmptyState
            icon="file"
            title="Nenhuma versao cadastrada"
            description="O texto e da seguradora. Abra um rascunho para carregar o primeiro."
          />
        }
      />

      {acao?.tipo === 'ver' ? (
        <Modal
          open
          onClose={() => setAcao(null)}
          size="lg"
          title={acao.versao.title || `Versao ${acao.versao.version}`}
          description={`Versao ${acao.versao.version}. Resumo SHA-256 ${acao.versao.body_sha256}.`}
        >
          <RichTextView html={acao.versao.body} />
          {acao.versao.notes ? (
            <p className="mt-4 text-xs text-ink-500">Observacao: {acao.versao.notes}</p>
          ) : null}
        </Modal>
      ) : null}
      {acao?.tipo === 'editar' ? (
        <EditorDeVersao
          versao={acao.versao}
          base={ativa ?? null}
          serie={serie}
          onFechar={() => setAcao(null)}
          onGravado={aposMudar}
        />
      ) : null}
      {acao?.tipo === 'ativar' || acao?.tipo === 'revogar' ? (
        <DecisaoDaVersao
          acao={acao.tipo}
          versao={acao.versao}
          serie={serie}
          onFechar={() => setAcao(null)}
          onGravado={aposMudar}
        />
      ) : null}
    </Card>
  );
}

function EditorDeVersao({
  versao,
  base,
  serie,
  onFechar,
  onGravado,
}: {
  versao: TermsVersion | null;
  base: TermsVersion | null;
  serie: SerieDeTermo;
  onFechar: () => void;
  onGravado: () => void;
}) {
  const toast = useToast();
  // O rascunho novo parte da versao ativa, como o servidor faz.
  const origem = versao ?? base;
  const [titulo, setTitulo] = useState(origem?.title ?? '');
  const [texto, setTexto] = useState(origem?.body ?? '');
  const [observacao, setObservacao] = useState(versao?.notes ?? '');

  const gravar = useMutation({
    mutationFn: () => {
      const entrada = { title: titulo, body: texto, notes: observacao };
      return versao ? serie.editar(versao.id, entrada) : serie.criar(entrada);
    },
    onSuccess: () => {
      toast.success(versao ? 'Rascunho atualizado.' : 'Rascunho aberto.');
      onGravado();
    },
  });
  const apiError = gravar.error instanceof ApiError ? gravar.error : null;

  return (
    <Modal
      open
      onClose={onFechar}
      size="lg"
      title={versao ? `Editar o rascunho da versao ${versao.version}` : 'Novo rascunho'}
      description="Quem escreve o rascunho nao o ativa: a ativacao e de outra pessoa."
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          gravar.mutate();
        }}
      >
        {gravar.error && !apiError?.details?.field ? (
          <Alert tone="danger" title="Nao foi possivel gravar">
            {gravar.error.message}
          </Alert>
        ) : null}
        <Field
          label="Titulo da versao"
          value={titulo}
          maxLength={200}
          error={fieldErrorOf(apiError, 'title')}
          onChange={(evento) => setTitulo(evento.target.value)}
        />
        <RichTextEditor
          label="Texto do termo"
          value={texto}
          onChange={setTexto}
          rows={12}
          error={fieldErrorOf(apiError, 'body')}
        />
        <Textarea
          label="Observacao da versao"
          value={observacao}
          error={fieldErrorOf(apiError, 'notes')}
          onChange={(evento) => setObservacao(evento.target.value)}
        />
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={gravar.isPending}>
            Salvar rascunho
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function DecisaoDaVersao({
  acao,
  versao,
  serie,
  onFechar,
  onGravado,
}: {
  acao: 'ativar' | 'revogar';
  versao: TermsVersion;
  serie: SerieDeTermo;
  onFechar: () => void;
  onGravado: () => void;
}) {
  const toast = useToast();
  const [texto, setTexto] = useState('');
  const ativando = acao === 'ativar';
  const decidir = useMutation({
    mutationFn: () =>
      ativando ? serie.ativar(versao.id, texto) : serie.revogar(versao.id, texto),
    onSuccess: () => {
      toast.success(ativando ? 'Versao ativada.' : 'Versao revogada.');
      onGravado();
    },
  });
  const apiError = decidir.error instanceof ApiError ? decidir.error : null;
  const campo = ativando ? 'justification' : 'reason';

  return (
    <Modal
      open
      onClose={onFechar}
      title={
        ativando
          ? `Ativar a versao ${versao.version}`
          : `Revogar a versao ${versao.version}`
      }
      description={
        ativando
          ? 'A versao entra em vigor agora e a anterior passa a revogada. Quem ja aceitou a anterior precisa aceitar de novo.'
          : 'A versao sai de vigor sem substituta. Ninguem precisa aceitar nada ate a proxima ativacao.'
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          decidir.mutate();
        }}
      >
        {decidir.error && !fieldErrorOf(apiError, campo) ? (
          <Alert
            tone="danger"
            title={ativando ? 'Nao foi possivel ativar' : 'Nao foi possivel revogar'}
          >
            {decidir.error.message}
          </Alert>
        ) : null}
        {ativando ? (
          <Textarea
            label="Justificativa da ativacao"
            required
            value={texto}
            error={fieldErrorOf(apiError, campo)}
            onChange={(evento) => setTexto(evento.target.value)}
          />
        ) : (
          <Textarea
            label="Motivo da revogacao"
            required
            value={texto}
            error={fieldErrorOf(apiError, campo)}
            onChange={(evento) => setTexto(evento.target.value)}
          />
        )}
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onFechar}>
            Fechar
          </Button>
          <Button
            type="submit"
            variant={ativando ? 'primary' : 'danger'}
            loading={decidir.isPending}
            disabled={texto.trim() === ''}
          >
            {ativando ? 'Ativar' : 'Revogar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
